import { ApiRouts } from '@/src/routes/routes';
import { BasicProductDataResponse } from '@/src/types/catalog';
import {
  IBranch,
  IBranchEta,
  ICloseBranch,
  IStore,
  IStoreCategories,
  IStoreCoupon,
  IStoreDepartment,
  IStoresIndustryGroup,
  IStoreSubNavigationSection,
} from '@/src/types/stores';
import HttpCLient from '@/src/lib/utils/httpClient';
import { ICmsSection } from '../../(cms)/CMSComponents/services/types';
import { IParams } from '@/src/lib/utils/createParams';

export type StoreParmas = {
  page?: string | number;
  pageCount?: string | number;
  q?: string;
  departmentIds?: string[] | number[] | string;
  departmentsIds?: string[] | number[] | string;
  orderType?: string;
  latitude?: string | number;
  longitude?: string | number;
  hasOffers?: boolean | string;
  isPrime?: boolean | string;
  isFastDelivery?: boolean | string;
  city?: string;
  status?: string;
};

export type ProductParams = {
  page?: string | number;
  pageCount?: string | number;
  search?: string;
  industryId?: string;
  categoryId?: string;
  storeId?: string;
  PriceLowToHigh?: boolean;
  CategoryIds?: string | string[];
  IndustryIds?: string | string[];
  haveOffers?: boolean | string;
  SortBy?: string;
  direction?: 'asc' | 'desc';
};

export type SearchProductParams = {
  page?: string | number;
  pageCount?: string | number;
  search?: string;
  haveOffers?: boolean | string;
};

class StoresAPI extends HttpCLient {
  // get all stores
  getStores = async (params?: StoreParmas) => {
    try {
      const depts = params?.departmentIds ?? params?.departmentsIds;
      const queryParams: IParams = {
        page: params?.page ? String(params.page) : '1',
        pageCount: params?.pageCount ? String(params.pageCount) : '50',
      };

      if (depts) {
        queryParams.departmentIds = Array.isArray(depts) ? depts : [depts];
      }
      if (params?.hasOffers !== undefined) {
        queryParams.hasOffers = params.hasOffers === 'true' || params.hasOffers === true;
      }
      if (params?.q) {
        queryParams.q = params.q;
      }
      if (params?.orderType) {
        queryParams.orderType = params.orderType;
      }
      if (params?.latitude !== undefined) {
        queryParams.latitude = params.latitude;
      }
      if (params?.longitude !== undefined) {
        queryParams.longitude = params.longitude;
      }
      if (params?.isPrime !== undefined) {
        queryParams.isPrime = params.isPrime === 'true' || params.isPrime === true;
      }
      if (params?.isFastDelivery !== undefined) {
        queryParams.isFastDelivery =
          params.isFastDelivery === 'true' || params.isFastDelivery === true;
      }
      if (params?.city) {
        queryParams.city = params.city;
      }
      if (params?.status) {
        queryParams.status = params.status;
      }

      const response = await this.get<IStore[]>(ApiRouts.stores, queryParams, {
        arrayFormat: 'repeat',
        next: {
          tags: ['stores'],
        },
      });

      return (JSON.parse(JSON.stringify(response)) as IStore[]) || [];
    } catch (error) {
      throw error;
    }
  };

  // get branches ETA
  getBranchesEta = async (data: { branchIds: number[]; latitude: number; longitude: number }) => {
    try {
      const response = await this.post<IBranchEta[]>(ApiRouts.branchesEta, data);
      return response || [];
    } catch (error) {
      throw error;
    }
  };

  // get store industries

  getStoresIndustries = async () => {
    try {
      const response = await this.get<IStoresIndustryGroup[]>(
        ApiRouts.storesIndustries,
        {},
        {
          next: {
            tags: ['stores-industries'],
          },
        },
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  getStoresDepartments = async () => {
    return await this.get<IStoreDepartment[]>(ApiRouts.storeDepatments);
  };

  // get closest branchid from storeId
  getClosestBranch = async (storeId: string) => {
    try {
      const response = await this.get<ICloseBranch>(
        ApiRouts.closestBranch(storeId),
        {},
        {
          next: {
            tags: ['closest-branch'],
          },
        },
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  // get branch details

  getBranch = async (branchId: string) => {
    const response = await this.get<IBranch>(
      ApiRouts.branch(branchId),
      {},
      {
        next: {
          tags: ['branch-details'],
        },
      },
    );
    return response;
  };

  getStoreCategories = async (branchId: string): Promise<IStoreCategories | null> => {
    try {
      const response = await this.get<IStoreCategories>(
        ApiRouts.storeCategories(branchId),
        {},
        { ignore404: true },
      );
      if ((response as any)?.status === 404) return null;
      return response;
    } catch (error) {
      return null;
    }
  };

  getStoreCoupons = async (branchId: string): Promise<IStoreCoupon[]> => {
    try {
      const response = await this.get<IStoreCoupon[]>(
        ApiRouts.storeCoupons(branchId),
        {},
        { ignore404: true },
      );
      if ((response as any)?.status === 404) return [];
      return response;
    } catch (error) {
      return [];
    }
  };

  getClosestBranchDetails = async (storeId: string) => {
    const closest = await this.getClosestBranch(storeId);
    if (!closest?.branchId) throw new Error('No branch found');
    const details = await this.getBranch(closest.branchId);
    return details;
  };

  getBranchId = async (storeId: string) => await this.getClosestBranch(storeId);

  // get all products for branch
  getAllProductsForBranch = async (branchId: string, params?: ProductParams) => {
    try {
      const queryParams: IParams = {
        page: params?.page ? String(params.page) : '1',
        pageCount: params?.pageCount ? String(params.pageCount) : '50',
      };

      if (params?.SortBy) {
        queryParams.SortBy = params.SortBy;
      }

      if (params?.direction) {
        queryParams.direction = params.direction;
      }

      if (params?.haveOffers !== undefined) {
        queryParams.haveOffers = params.haveOffers;
      }

      if (params?.search) {
        queryParams.search = params.search;
      }

      if (params?.storeId) {
        queryParams.storeId = params.storeId;
      }

      const categoryIds = params?.CategoryIds
        ? Array.isArray(params.CategoryIds)
          ? params.CategoryIds
          : [params.CategoryIds]
        : params?.categoryId
          ? [params.categoryId]
          : undefined;

      const industryIds = params?.IndustryIds
        ? Array.isArray(params.IndustryIds)
          ? params.IndustryIds
          : [params.IndustryIds]
        : params?.industryId
          ? [params.industryId]
          : undefined;

      const body: Record<string, any> = {};

      if (categoryIds && categoryIds.length > 0) {
        body.CategoryIds = categoryIds;
      }

      if (industryIds && industryIds.length > 0) {
        body.IndustryIds = industryIds;
      }

      const response = await this.post<BasicProductDataResponse[] | any>(
        ApiRouts.filteredProducts(branchId),
        body,
        {
          headers: {
            tenantId: '1',
            Accept: 'application/json',
          },
          next: {
            tags: ['products'],
          },
          ignore404: true,
        },
        queryParams,
      );

      if (response?.status === 404) {
        return [];
      }

      return response;
    } catch (error) {
      if (error instanceof Error) {
        return [];
      }
    }
  };

  // search products for branch using ApiRouts.allProductsForBranch
  searchProductsForBranch = async (branchId: string, params?: SearchProductParams) => {
    try {
      const queryParams: IParams = {
        page: params?.page ? String(params.page) : '1',
        pageCount: params?.pageCount ? String(params.pageCount) : '50',
      };

      if (params?.search) {
        queryParams.search = params.search;
      }

      if (params?.haveOffers !== undefined) {
        queryParams.haveOffers = params.haveOffers;
      }

      const response = await this.get<BasicProductDataResponse[] | any>(
        ApiRouts.allProductsForBranch(branchId),
        queryParams,
        {
          headers: {
            tenantId: '1',
            Accept: 'application/json',
          },
          next: {
            tags: ['products'],
          },
          ignore404: true,
        },
      );

      if (response?.status === 404) {
        return [];
      }

      return response ?? [];
    } catch (error) {
      if (error instanceof Error) {
        return [];
      }
      return [];
    }
  };

  // get all products for branch with category
  getAllProductsForBranchWithCategory = async (
    branchId: string,
    categoryId: string,
    params?: ProductParams,
  ) => {
    try {
      const defaultParams = {
        ...params,
        page: params?.page || '1',
        pageCount: params?.pageCount || '1000',
      };

      const response = await this.get<BasicProductDataResponse[] | any>(
        ApiRouts.allProductsForBranchWithCategory(branchId, categoryId),
        defaultParams,
        {
          next: {
            tags: ['products'],
          },
        },
      );
      if (response?.status === 404) {
        return [];
      }
      return response ?? [];
    } catch (error) {
      throw error;
    }
  };

  getStoreCms = async (storeId: string, branchId: string) => {
    try {
      const response = await this.get<ICmsSection[]>(
        ApiRouts.storeCms(storeId, branchId),
        {},
        { ignore404: true },
      );
      if ((response as any)?.status === 404) return [];
      return response;
    } catch (error) {
      return [];
    }
  };

  getSubNavigationSections = async (params: {
    StoreType: string;
    Id: string;
    BranchId?: string;
  }): Promise<IStoreSubNavigationSection[]> => {
    try {
      const response = await this.get<IStoreSubNavigationSection[]>(
        ApiRouts.subNavigationSections,
        params,
        { ignore404: true },
      );
      if ((response as any)?.status === 404) return [];
      return response;
    } catch (error) {
      return [];
    }
  };
}

export const storesApi = new StoresAPI();
